import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Square, ArrowRightLeft, MapPin, User, CloudSun, Video } from "lucide-react";
import { endpoints } from "@/lib/api";
import { TeamLogo, FavStar } from "@/components/football/MatchRow";
import { HighlightCard, HighlightPlayer } from "@/components/football/Highlights";

const TABS = [["timeline", "Timeline"], ["lineups", "Lineups"], ["stats", "Statistics"], ["info", "Match info"], ["highlights", "Highlights"]];

const EventIcon = ({ t = "" }) => {
  t = t.toLowerCase();
  if (t.includes("goal") || t.includes("penalty")) return <span className="w-5 h-5 rounded-full bg-green-500/20 text-green-400 inline-flex items-center justify-center text-[10px] font-black">G</span>;
  if (t.includes("yellow")) return <Square className="w-4 h-4 text-yellow-400 fill-yellow-400" />;
  if (t.includes("red")) return <Square className="w-4 h-4 text-red-500 fill-red-500" />;
  if (t.includes("substitution")) return <ArrowRightLeft className="w-4 h-4 text-sky-400" />;
  return <span className="w-2 h-2 rounded-full bg-slate-500 inline-block" />;
};

const Timeline = ({ m }) => (m.events?.length || 0) === 0 ? <p className="text-slate-400 text-center py-10">No match events yet.</p> : (
  <div className="space-y-2" data-testid="match-events">
    {m.events.map((e, i) => (
      <div key={i} className={`flex items-center gap-2 text-sm ${e.side === "away" ? "flex-row-reverse text-right" : ""}`}>
        <span className="text-slate-400 w-10 shrink-0 tabular-nums">{e.minute != null ? `${e.minute}${e.extra ? `+${e.extra}` : ""}'` : ""}</span>
        <EventIcon t={e.type} />
        <span className="text-slate-100 min-w-0"><b>{e.player || e.type}</b>
          {e.related && <span className="text-slate-400"> {e.typeCode === "substitution" ? "↔" : "· assist"} {e.related}</span>}
          {e.result && <span className="text-slate-400 ml-1">({e.result})</span>}
          {e.info && <span className="text-slate-500 ml-1 text-xs">{e.info}</span>}
        </span>
      </div>
    ))}
  </div>
);

const Side = ({ title, side, formation, coach }) => (
  <div className="rounded-xl border border-[#273042] bg-[#121620] p-4">
    <div className="text-white font-bold mb-1">{title} {formation && <span className="text-xs text-slate-400 font-normal">· {formation}</span>}</div>
    {coach && <div className="text-xs text-slate-400 mb-3">Coach: {coach}</div>}
    {[["Starting XI", side.starting], ["Substitutes", side.bench]].map(([l, arr]) => (
      <div key={l} className="mb-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">{l}</div>
        {arr.map((p) => (
          <Link key={p.playerId || p.name} to={p.playerId ? `/football/player/${p.playerId}` : "#"} className="flex items-center gap-2 py-1 text-sm text-slate-200 hover:text-white">
            <span className="w-6 text-right text-slate-500 tabular-nums">{p.number}</span>
            {p.photoUrl ? <img src={p.photoUrl} alt="" className="w-6 h-6 rounded-full object-cover bg-[#0A0C10]" loading="lazy" /> : <span className="w-6 h-6 rounded-full bg-[#0A0C10]" />}
            <span className="truncate">{p.name}</span><span className="ml-auto text-[11px] text-slate-500">{p.position}</span>
          </Link>
        ))}
      </div>
    ))}
  </div>
);

const Stats = ({ m }) => (m.statistics?.length || 0) === 0 ? <p className="text-slate-400 text-center py-10">No statistics yet.</p> : (
  <div className="space-y-3" data-testid="match-stats">
    {m.statistics.map((s) => {
      const h = Number(s.home) || 0, a = Number(s.away) || 0, t = h + a || 1;
      return (
        <div key={s.name}>
          <div className="flex justify-between text-sm text-white"><b>{s.home ?? 0}</b><span className="text-slate-400 text-xs">{s.name}</span><b>{s.away ?? 0}</b></div>
          <div className="flex gap-1 h-1.5 mt-1"><div className="bg-[#E11D2A] rounded-l" style={{ width: `${(h / t) * 100}%` }} /><div className="bg-sky-500 rounded-r" style={{ width: `${(a / t) * 100}%` }} /></div>
        </div>
      );
    })}
  </div>
);

const Info = ({ m }) => (
  <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="match-info">
    {m.venue && <div className="rounded-xl border border-[#273042] bg-[#121620] p-4"><MapPin className="w-4 h-4 text-[#E11D2A] mb-1" /><b className="text-white">{m.venue.name}</b><div className="text-slate-400">{m.venue.city}{m.venue.capacity ? ` · capacity ${m.venue.capacity.toLocaleString()}` : ""}</div></div>}
    {m.referees?.length > 0 && <div className="rounded-xl border border-[#273042] bg-[#121620] p-4"><User className="w-4 h-4 text-[#E11D2A] mb-1" />{m.referees.map((r) => <div key={r.name + r.role} className="text-slate-200">{r.name} <span className="text-slate-500 text-xs">{r.role}</span></div>)}</div>}
    {m.weather && <div className="rounded-xl border border-[#273042] bg-[#121620] p-4"><CloudSun className="w-4 h-4 text-[#E11D2A] mb-1" /><div className="text-slate-200 capitalize">{m.weather.description} · {Math.round(m.weather.temp)}°C</div><div className="text-slate-400 text-xs">Wind {m.weather.wind} m/s · Humidity {m.weather.humidity}</div></div>}
    <div className="rounded-xl border border-[#273042] bg-[#121620] p-4 text-slate-300">{m.league}{m.stage ? ` · ${m.stage}` : ""}{m.round ? ` · Round ${m.round}` : ""}<div className="text-slate-400 text-xs mt-1">{m.date} UTC</div></div>
  </div>
);

export default function FootballMatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("timeline");
  const [play, setPlay] = useState(null);
  useEffect(() => {
    let alive = true;
    endpoints.footballMatch(id).then((r) => { if (alive) setM(r.data); }).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [id]);
  // Live refresh: every 60s, only while the match is live and the tab is visible.
  useEffect(() => {
    if (m?.status !== "live") return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") endpoints.footballMatch(id).then((r) => setM(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [id, m?.status]);

  if (err) return <div className="text-center text-slate-300 py-24" data-testid="match-error">Match details are unavailable.</div>;
  if (!m) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="match-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="rounded-2xl bg-gradient-to-br from-[#1a0c0e] to-[#121620] border border-[#273042] p-6 mb-6">
        <div className="text-center text-xs text-slate-400 mb-4">{m.league}{m.round ? ` · Round ${m.round}` : ""}</div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 flex flex-col items-center gap-1"><Link to={`/football/team/${m.home?.id}`} className="flex flex-col items-center gap-2 text-white font-bold text-center"><TeamLogo team={m.home} size="w-14 h-14" />{m.home?.name}</Link>{m.home?.id && <FavStar kind="teams" testid="fav-team-home" item={{ id: m.home.id, name: m.home.name, logoUrl: m.home.logoUrl }} />}</div>
          <div className="text-center shrink-0">
            <div className="text-4xl font-black text-white tabular-nums" data-testid="match-score">{m.status === "scheduled" ? "vs" : `${m.homeScore ?? 0} : ${m.awayScore ?? 0}`}</div>
            <div className={`text-xs font-bold mt-1 ${m.status === "live" ? "text-[#EF4444]" : "text-slate-400"}`}>{m.statusLabel}</div>
          </div>
          <div className="flex-1 flex flex-col items-center gap-1"><Link to={`/football/team/${m.away?.id}`} className="flex flex-col items-center gap-2 text-white font-bold text-center"><TeamLogo team={m.away} size="w-14 h-14" />{m.away?.name}</Link>{m.away?.id && <FavStar kind="teams" testid="fav-team-away" item={{ id: m.away.id, name: m.away.name, logoUrl: m.away.logoUrl }} />}</div>
        </div>
      </div>
      <div className="flex gap-1 border-b border-[#273042] mb-5 overflow-x-auto">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`match-tab-${k}`}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "text-white border-[#E11D2A]" : "text-slate-400 border-transparent hover:text-white"}`}>{l}</button>)}
      </div>
      {tab === "timeline" && <Timeline m={m} />}
      {tab === "lineups" && (m.lineups?.home?.starting?.length ? (
        <div className="grid md:grid-cols-2 gap-4" data-testid="match-lineups">
          <Side title={m.home?.name} side={m.lineups.home} formation={m.formations?.home} coach={m.coaches?.home} />
          <Side title={m.away?.name} side={m.lineups.away} formation={m.formations?.away} coach={m.coaches?.away} />
        </div>) : <p className="text-slate-400 text-center py-10">Lineups are published about an hour before kick-off.</p>)}
      {tab === "stats" && <Stats m={m} />}
      {tab === "info" && <Info m={m} />}
      {tab === "highlights" && (m.highlights?.length ? (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="match-highlights">{m.highlights.map((h) => <HighlightCard key={h.id} h={h} onPlay={setPlay} wide />)}</div>
      ) : <div className="text-center py-10 text-slate-400" data-testid="match-highlights-empty"><Video className="w-8 h-8 mx-auto mb-2 text-slate-500" />{m.highlightsNote}</div>)}
      {play && <HighlightPlayer h={play} onClose={() => setPlay(null)} />}
    </div>
  );
}
